import React, { useCallback, useState } from 'react';
import { Download, FileText, Loader2, RefreshCw, Clock, CalendarClock } from 'lucide-react';
import { commsApi } from '@/utils/commsApi';
import { formatBytes, formatTime, isImage } from '@/utils/commsFormat';
import { useLongPress } from '@/hooks/useLongPress';
import { VoiceClip } from './VoiceClip';
import { SeenMarker } from './SeenMarker';
import { PlayedMarker } from './PlayedMarker';
import { ReactionPicker, ReactionPills } from './Reactions';
import { ListenButton } from '@/components/ListenButton';

const CAN_HOVER = typeof window !== 'undefined' && window.matchMedia?.('(hover: hover)').matches;

const BubbleBody = ({ msg, mine, live, onOpenImage, onPlayed }) => {
  const info = msg.file_info;
  if (msg.message_type === 'voice' && info?.file_id) {
    return (
      <VoiceClip
        fileId={info.file_id}
        duration={info.duration}
        live={live}
        transcript={msg.transcript}
        transcriptStatus={msg.transcript_status}
        onPlayed={mine ? undefined : () => onPlayed?.(msg)}
      />
    );
  }
  if (msg.message_type === 'file' && info?.file_id) {
    const url = commsApi.fileUrl(info.file_id);
    if (isImage(info)) {
      return (
        <img
          className="thumb"
          src={url}
          alt={info.filename}
          loading="lazy"
          onClick={() => onOpenImage(url)}
          data-testid="image-attachment"
        />
      );
    }
    return (
      <a className="file-card" href={url} target="_blank" rel="noreferrer" download={info.filename} data-testid="file-attachment">
        <FileText size={22} />
        <span className="file-text">
          <span className="file-name">{info.filename}</span>
          <span className="file-size">{formatBytes(info.size)}</span>
        </span>
        <Download size={16} />
      </a>
    );
  }
  return <span data-testid="message-text">{linkify(msg.message)}</span>;
};

const URL_RE = /(https?:\/\/[^\s<>"')\]]+)/g;
// Make bare URLs clickable; same-origin links open in this tab, others in a new one.
const linkify = (text = '') => text.split(URL_RE).map((part, i) => {
  if (i % 2 === 0) return part;
  const internal = part.startsWith(window.location.origin);
  return (
    <a key={`${i}-${part}`} href={part} className="message-link" target={internal ? undefined : '_blank'} rel={internal ? undefined : 'noreferrer'} data-testid="message-link">
      {internal ? part.replace(window.location.origin, '') : part}
    </a>
  );
});

export const MessageBubble = ({
  msg, mine, live, highlighted, showSender, seen, members, channel, userId, onRetry, onOpenImage, onPlayed, onReact, onSnooze,
}) => {
  const failed = msg.status === 'failed';
  const sending = msg.status === 'sending';
  const isVoice = msg.message_type === 'voice';
  const canReact = Boolean(onReact) && !failed && !sending;
  const canSnooze = Boolean(onSnooze) && !mine && Boolean(msg.reminder_id) && !failed && !sending;
  const [snoozing, setSnoozing] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const closePicker = useCallback(() => setPickerOpen(false), []);
  const longPress = useLongPress(() => canReact && setPickerOpen(true));
  const pick = (emoji) => { setPickerOpen(false); onReact(msg, emoji); };
  const hoverProps = CAN_HOVER && canReact ? { onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) } : {};
  const snooze = async (mode) => {
    if (snoozing) return;
    setSnoozing(true);
    try { await onSnooze(msg, mode); } finally { setSnoozing(false); }
  };

  return (
    <div className={`msg-row ${mine ? 'mine' : ''}`} data-testid={`message-${msg.id}`} {...hoverProps}>
      <div className="msg-col">
        <div className="bubble-wrap" {...(canReact ? longPress : {})}>
          <div className={`bubble ${failed ? 'failed' : ''} ${live ? 'playing' : ''} ${highlighted ? 'highlighted' : ''}`}>
            {showSender && !mine && (
              <div className="bubble-sender" data-testid="message-sender">{msg.from_user_name || 'Teammate'}</div>
            )}
            <BubbleBody msg={msg} mine={mine} live={live} onOpenImage={onOpenImage} onPlayed={onPlayed} />
            <div className="bubble-meta">
              {msg.message_type === 'text' && msg.message && !failed && !sending && (
                <ListenButton text={msg.message} id={`msg-${msg.id}`} compact className="bubble-listen" testId={`listen-${msg.id}`} />
              )}
              {sending && <Loader2 size={11} className="spin" />}
              {failed ? (
                <button type="button" className="retry-btn" onClick={() => onRetry(msg)} data-testid={`retry-${msg.client_id}`}>
                  <RefreshCw size={11} /> Failed · Tap to retry
                </button>
              ) : (
                <span>{sending ? 'Sending…' : formatTime(msg.created_at)}</span>
              )}
            </div>
          </div>
          {canReact && (pickerOpen || hovered) && (
            <ReactionPicker messageId={msg.id} onPick={pick} onClose={closePicker} />
          )}
        </div>
        {canSnooze && (
          <div className="reminder-actions" data-testid={`reminder-actions-${msg.id}`}>
            <button type="button" className="reminder-snooze" disabled={snoozing} onClick={() => snooze('hour')} data-testid={`snooze-hour-${msg.id}`}>
              <Clock size={12} /> Snooze 1 h
            </button>
            <button type="button" className="reminder-snooze" disabled={snoozing} onClick={() => snooze('tomorrow')} data-testid={`snooze-tomorrow-${msg.id}`}>
              <CalendarClock size={12} /> Snooze tomorrow
            </button>
          </div>
        )}
        <ReactionPills reactions={msg.reactions} userId={userId} messageId={msg.id} onToggle={(emoji) => onReact?.(msg, emoji)} />
        {mine && isVoice && <PlayedMarker plays={msg.played_by} channel={channel} />}
        {mine && !isVoice && seen && <SeenMarker seen={seen} members={members} />}
      </div>
    </div>
  );
};
